import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface OnFloor {
  staffId: string;
  staffName: string;
  role: string;
  since: string;
  hoursOnShift: string;
}

/**
 * Clocking in and out, plus who is on the floor right now. Separate from
 * till shifts — production staff clock in without ever opening a till.
 */
export default function AttendancePanel({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName?: string;
}) {
  const [onFloor, setOnFloor] = useState<OnFloor[]>([]);
  const [myStats, setMyStats] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const [who, stats] = await Promise.all([
        apiClient.whoIsIn(branchId),
        apiClient.getMyAttendanceStats().catch(() => ({ data: null })),
      ]);
      setOnFloor(Array.isArray(who.data) ? who.data : []);
      setMyStats(stats.data);
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load attendance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // keep "who's in" current
    return () => clearInterval(t);
  }, [branchId]);

  const clock = async (dir: 'in' | 'out') => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const res = dir === 'in' ? await apiClient.clockIn(branchId) : await apiClient.clockOut(branchId);
      // Server runs in UTC; format the time here so it reads in local time
      const at = res.data?.timestamp
        ? ` at ${new Date(res.data.timestamp).toLocaleTimeString('en-ZA', { timeStyle: 'short' })}`
        : '';
      setMsg(`${res.data?.message || 'Done'}${at}`);
      await load();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900">Clock in or out</h3>
          <p className="text-sm text-gray-500 mt-1">Records against your own name.</p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => clock('in')}
              disabled={busy}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
            >
              Clock in
            </button>
            <button
              onClick={() => clock('out')}
              disabled={busy}
              className="flex-1 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
            >
              Clock out
            </button>
          </div>

          {myStats && (
            <div className="mt-5 pt-4 border-t text-sm space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your last 30 days</p>
              <div className="flex justify-between"><span className="text-gray-600">Days worked</span><span className="tabular-nums font-semibold">{myStats.daysWorked}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Hours</span><span className="tabular-nums font-semibold">{myStats.hoursWorked}</span></div>
              {myStats.unpairedShifts > 0 && (
                <p className="text-xs text-amber-700 pt-1">
                  {myStats.unpairedShifts} shift{myStats.unpairedShifts === 1 ? '' : 's'} with no clock-out
                </p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow lg:col-span-2">
          <div className="px-6 py-4 border-b flex justify-between items-baseline">
            <div>
              <h3 className="text-lg font-medium text-gray-900">On the floor now</h3>
              <p className="text-sm text-gray-500">{branchName}</p>
            </div>
            <span className="tabular-nums font-bold text-2xl text-gray-900">{onFloor.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {onFloor.length === 0 ? (
                  <tr><td className="py-10 text-center text-gray-500">Nobody is clocked in.</td></tr>
                ) : (
                  onFloor.map((p) => (
                    <tr key={p.staffId} className="border-b last:border-0">
                      <td className="py-3 px-6">
                        <p className="font-medium text-gray-900">{p.staffName}</p>
                        <p className="text-xs text-gray-500 capitalize">{p.role.toLowerCase()}</p>
                      </td>
                      <td className="py-3 px-4 text-gray-600 tabular-nums">
                        since {new Date(p.since).toLocaleTimeString('en-ZA', { timeStyle: 'short' })}
                      </td>
                      <td className="py-3 px-6 text-right tabular-nums font-semibold text-gray-900">
                        {p.hoursOnShift} hrs
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
